import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowUp, ArrowUpRight, BookOpen, Box, Boxes, Check, ChevronRight, CircleHelp, MapPin, MessageSquare, RotateCcw, Search, ShieldCheck, Square, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import "./assistant.css";

const AssistantContext = createContext(null);
const MODES = { ask: ["Frage", MessageSquare], articles: ["Artikel", Box], lots: ["Posten", Boxes], storage: ["Lager", MapPin], help: ["Hilfe", BookOpen] };
const STARTERS = [
  { mode: "storage", title: "Wo liegt mein Artikel?", hint: "SKU eingeben und Lagerplatz finden", icon: MapPin },
  { mode: "lots", title: "Was gehört zu einem Posten?", hint: "Postennummer oder Namen nachschlagen", icon: Boxes },
  { mode: "help", title: "Wie bearbeite ich eine Retoure?", hint: "Den Ablauf mit Quellen verstehen", icon: BookOpen, question: "Wie bearbeite ich eine Retoure?", article: "returns" },
];
const date = value => {
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d) : "Zeitpunkt unbekannt";
};
const amount = value => typeof value === "number" && Number.isFinite(value) ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value) : "nicht bekannt";

function safeScreen(value) {
  return typeof value === "string" && /^(\/artikel(?:\?search=[^#]*)?|\/posten|\/lagerverwaltung|\/paletten|\/retouren|\/)$/u.test(value) ? value : null;
}

function errorMessage(error) {
  const code = error?.response?.status;
  if (code === 403) return "Für diese Anfrage fehlt die Leseberechtigung. Frühere Ergebnisse wurden zum Schutz Ihrer Daten entfernt.";
  if (code === 401) return "Ihre Sitzung ist abgelaufen. Bitte erneut anmelden.";
  if (code === 422 || code === 413) return "Bitte eine Frage mit 2–500 Zeichen eingeben. Suchbegriffe dürfen höchstens 80 Zeichen lang sein.";
  if (code === 404) return "Der Assistent ist derzeit nicht aktiviert.";
  return "Die Suche ist gerade nicht erreichbar. Versuchen Sie es erneut oder verwenden Sie die Bedienhilfe.";
}

export function AssistantProvider({ children }) {
  const auth = useAuth();
  // Remount synchronously on account/permission changes. No previous account's
  // history can flash for a render; nothing is persisted in browser storage.
  const identity = JSON.stringify([auth.user?.id, auth.user?.role, auth.user?.custom_role, auth.modulesLoaded, auth.modules, auth.finance]);
  return <AssistantSession key={identity} auth={auth}>{children}</AssistantSession>;
}

function AssistantSession({ auth, children }) {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("ask");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null), trigger = useRef(null), scroll = useRef(null);
  const request = useRef({ version: 0, controller: null });
  const counter = useRef(0);
  const location = useLocation();

  useEffect(() => {
    if (!auth.user || !auth.modulesLoaded) return;
    const controller = new AbortController();
    api.get("/assistant/status", { signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setEnabled(data?.enabled === true);
    }).catch(() => {});
    return () => controller.abort();
  }, [auth.user, auth.modulesLoaded]);

  useEffect(() => {
    if (!open || catalog) return;
    const controller = new AbortController();
    setCatalogError("");
    api.get("/assistant/catalog", { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setCatalog(data);
      if (!data.modes.includes(mode)) setMode("ask");
    }).catch(() => {
      if (!controller.signal.aborted) setCatalogError("Die Bedienhilfe konnte nicht geladen werden.");
    });
    return () => controller.abort();
  }, [open, catalog, catalogAttempt, mode]);

  useEffect(() => () => { ++request.current.version; request.current.controller?.abort(); }, []);
  useEffect(() => {
    if (open && scroll.current) {
      const last = [...scroll.current.querySelectorAll('.wa-exchange')].at(-1);
      scroll.current.scrollTop = last ? Math.max(0, last.offsetTop - 6) : 0;
    }
  }, [open, history, busy]);

  const cancel = () => {
    ++request.current.version;
    request.current.controller?.abort();
    request.current.controller = null;
    setBusy(false);
    setHistory(prev => prev.map(row => row.pending ? { ...row, pending: false, cancelled: true } : row));
  };
  const changeOpen = value => {
    if (!value) cancel();
    setOpen(value);
  };
  const openAssistant = element => { trigger.current = element; setOpen(true); };

  const ask = async (question = draft, selectedMode = mode, objectId = null) => {
    const query = question.trim();
    if (!catalog || request.current.controller || query.length < 2 || query.length > 500) return;
    const id = ++counter.current;
    const controller = new AbortController();
    const version = ++request.current.version;
    request.current.controller = controller;
    const current = () => version === request.current.version && !controller.signal.aborted;
    setBusy(true);
    setHistory(prev => [...prev.slice(-11), { id, question: query, pending: true }]);
    setDraft("");
    try {
      const { data } = await api.post("/assistant/query", { mode: selectedMode, query, ...(objectId ? { object_id: objectId } : {}) }, { signal: controller.signal, timeout: 12000 });
      if (!current()) return;
      setHistory(prev => prev.map(row => row.id === id ? { ...row, pending: false, result: data } : row));
    } catch (error) {
      if (!current()) return;
      const message = { id, question: query, error: errorMessage(error) };
      if ([401, 403].includes(error?.response?.status)) {
        setHistory([message]);
        setCatalog(null);
        auth.refresh();
      } else {
        setHistory(prev => prev.map(row => row.id === id ? message : row));
      }
    } finally {
      if (current()) { request.current.controller = null; setBusy(false); input.current?.focus(); }
    }
  };

  const readHelp = article => {
    if (request.current.controller) return;
    setHistory(prev => [...prev.slice(-11), { id: ++counter.current, question: article.title,
      result: { kind: "help", mode: "local", read_at: new Date().toISOString(), articles: [article], items: [] } }]);
    input.current?.focus();
  };

  return <AssistantContext.Provider value={{ enabled, openAssistant }}>
    {children}
    {enabled && auth.user && <Dialog.Root open={open} onOpenChange={changeOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="wp-assistant-overlay" />
        <Dialog.Content className="wp-assistant-panel ph-no-capture" data-testid="assistant-panel"
          onOpenAutoFocus={event => { event.preventDefault(); input.current?.focus(); }}
          onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.isConnected && trigger.current.focus(); }}>
          <header className="wa-header">
            <span className="wa-mark" aria-hidden="true"><MessageSquare size={23}/></span>
            <div><span className="wa-eyebrow">HILFE IM ARBEITSALLTAG</span><Dialog.Title>WarenPro Assistent</Dialog.Title></div>
            <Dialog.Close className="wa-icon-button" aria-label="Assistent schließen" data-testid="assistant-close"><X size={20}/></Dialog.Close>
          </header>
          <Dialog.Description className="wa-description">Artikel finden. Abläufe verstehen. Mit nachvollziehbaren Quellen.</Dialog.Description>
          <div className="wa-connection"><span><i aria-hidden="true"/>Lokale Hilfe & Suche</span><span>KI nicht verbunden</span></div>
          <div className="wa-scroll" ref={scroll} data-testid="assistant-history">
            {!history.length && <div className="wa-welcome">
              <div className="wa-welcome-symbol" aria-hidden="true"><Search size={27}/><span><Check size={12}/></span></div>
              <p className="wa-eyebrow">DEIN KURZER WEG ZUR ANTWORT</p>
              <h2>Was möchtest<br/>du nachschlagen?</h2>
              <p>Eine SKU, ein Posten oder eine Frage zum nächsten Arbeitsschritt.</p>
              {catalog && <div className="wa-starters">{STARTERS.filter(s => catalog.modes.includes(s.mode) && (!s.article || catalog.articles.some(a => a.id === s.article))).map(starter => <button key={starter.title} type="button" onClick={() => {
                if (starter.question) ask(starter.question, "help");
                else { setMode(starter.mode); input.current?.focus(); }
              }}><span className="wa-starter-icon"><starter.icon size={18}/></span><span><strong>{starter.title}</strong><small>{starter.hint}</small></span><ChevronRight size={16}/></button>)}</div>}
            </div>}
            {!catalog && !catalogError && <p className="wa-loading" role="status">Bedienhilfe wird geladen…</p>}
            {catalogError && <div role="alert" className="wa-error"><strong>Bedienhilfe nicht erreichbar</strong><p>{catalogError}</p><button onClick={() => setCatalogAttempt(n => n+1)}>Hilfe erneut laden</button></div>}
            {history.map(row => <section className="wa-exchange" key={row.id} aria-label="Frage und Antwort">
              <div className="wa-question"><span>DU</span><p>{row.question}</p></div>
              <div className="wa-answer">
                <span className="wa-answer-label"><MessageSquare size={14}/> WARENPRO <span>Lokale Antwort</span></span>
                {row.pending && <div className="wa-pending" role="status"><span aria-hidden="true">•••</span>Quellen werden nachgeschlagen…</div>}
                {row.cancelled && <p role="status" className="wa-muted">Anfrage abgebrochen. Du kannst eine neue Suche starten.</p>}
                {row.error && <div className="wa-error" role="alert"><strong>Anfrage nicht beantwortet</strong><p>{row.error}</p><button onClick={() => { setDraft(row.question); input.current?.focus(); }}>Frage übernehmen</button></div>}
                {row.result && <Answer result={row.result} onFollow={item => ask(`Artikel in ${item.number || item.title}`, "lots", item.id)} onNavigate={() => changeOpen(false)} disabled={busy}/>}
              </div>
            </section>)}
            {catalog && <details className="wa-library" open={!history.length}>
              <summary><BookOpen size={15}/>Bedienhilfe<span>{catalog.articles.length} Themen</span></summary>
              <div>{catalog.articles.map(article => <button key={article.id} disabled={busy} onClick={() => readHelp(article)}>{article.title}<ArrowUpRight size={14}/></button>)}</div>
            </details>}
            <p className="wa-privacy"><ShieldCheck size={14}/>Nur Nachschlagen. Verlauf im Arbeitsspeicher, maximal zwölf Fragen. Beim Abmelden wird er verworfen.</p>
          </div>
          <footer className="wa-footer">
            <div className="wa-composer-top"><div role="group" aria-label="Suchart" className="wa-modes">{(catalog?.modes || ["ask"]).map(key => {
              const [label, Icon] = MODES[key] || MODES.ask;
              return <button key={key} type="button" aria-pressed={mode === key} disabled={busy} onClick={() => { setMode(key); input.current?.focus(); }} data-testid={`assistant-mode-${key}`}><Icon size={13}/>{label}</button>;
            })}</div>{!!history.length && <button type="button" className="wa-icon-button wa-clear" disabled={busy} aria-label="Sitzungsverlauf leeren" onClick={() => { cancel(); setHistory([]); setDraft(""); input.current?.focus(); }}><RotateCcw size={16}/></button>}</div>
            <form className="wa-composer" onSubmit={event => { event.preventDefault(); ask(); }}>
              <label htmlFor="assistant-question">{mode === "ask" || mode === "help" ? "Deine Frage" : "Dein Suchbegriff"}</label>
              <textarea id="assistant-question" data-testid="assistant-input" ref={input} value={draft} maxLength={500} rows={2} autoComplete="off"
                placeholder={mode === "articles" ? "SKU, Name oder EAN …" : mode === "lots" ? "Postennummer oder Name …" : mode === "storage" ? "SKU, Palette oder Lagerort …" : "Zum Beispiel: Wie bearbeite ich eine Retoure?"}
                onChange={event => setDraft(event.target.value)} disabled={busy}
                onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); ask(); } }}/>
              <div className="wa-composer-bottom"><span>{busy ? "Suche läuft" : `${draft.length}/500 · Enter zum Senden`}</span>
                {busy ? <button type="button" className="wa-send wa-stop" onClick={cancel} aria-label="Anfrage abbrechen" data-testid="assistant-cancel"><Square size={13}/><span>Abbrechen</span></button> :
                  <button type="submit" className="wa-send" disabled={!catalog || draft.trim().length < 2} aria-label="Frage senden" data-testid="assistant-send"><ArrowUp size={19}/></button>}
              </div>
            </form>
            <div className="wa-footnote"><span>Nur lesend</span><span>{location.pathname === "/" ? "Dashboard" : "WarenPro"} · Quellenstand 03.11.2026</span></div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>}
  </AssistantContext.Provider>;
}

export function AssistantEntry() {
  const assistant = useContext(AssistantContext);
  if (!assistant?.enabled) return null;
  return <button type="button" className="wa-entry" data-testid="assistant-open" onClick={event => assistant.openAssistant(event.currentTarget)} aria-haspopup="dialog"><CircleHelp size={17}/><span>Assistent</span><span className="wa-entry-dot" aria-hidden="true"/></button>;
}

function ScreenLink({ href, children, onNavigate }) {
  const safe = safeScreen(href);
  return safe ? <Link to={safe} onClick={onNavigate}>{children}<ArrowUpRight size={13}/></Link> : null;
}

function Answer({ result, onFollow, onNavigate, disabled }) {
  return <div className="wa-result" data-testid="assistant-result">
    <p className={`wa-result-message ${["empty", "unknown", "unsupported"].includes(result.kind) ? "wa-notice" : ""}`}>{result.message}</p>
    {result.items?.length > 0 && <ul className="wa-items">{result.items.map(item => <li key={item.type + item.id} className="wa-item">
      <div className="wa-item-top"><span className="wa-item-icon">{item.type === "lot" ? <Boxes size={17}/> : item.type === "article" ? <Box size={17}/> : <MapPin size={17}/>}</span><div><span className="wa-item-code">{item.sku || item.number || (item.type === "pallet" ? "PALETTE" : "LAGERORT")}</span><strong>{item.title}</strong></div></div>
      {item.stock !== undefined && <div className="wa-stock"><span>Artikelbestand</span><strong>{amount(item.stock)} <small>{item.unit}</small></strong></div>}
      {item.archived && <p className="wa-notice">Archivierter Artikel · Zuordnung zum Posten bleibt erhalten.</p>}
      {item.stock_note && <p className="wa-notice">{item.stock_note}</p>}
      {item.status && <p className="wa-muted">Status: {item.status}</p>}
      {item.locations && (item.locations.length ? <div className="wa-locations">{item.locations.map((place, i) => <div key={i}><span><MapPin size={13}/><strong>{place.label}</strong></span><p>{place.warehouse || "Lager nicht bestätigt"}{place.pallet ? ` · ${place.pallet}` : ""}</p>{place.quantity !== undefined && <small>Zugeordnet: {amount(place.quantity)} {place.quantity_unit || item.unit}</small>}{place.warning && <p className="wa-notice">{place.warning}</p>}</div>)}</div> : item.type === "article" && <p className="wa-notice">Keine bestätigte Fachzuordnung hinterlegt.</p>)}
      {item.locations_more && <p className="wa-notice">Weitere Zuordnungen im Lagerarbeitsplatz prüfen.</p>}
      <div className="wa-item-actions">{item.type === "lot" && <button type="button" disabled={disabled} onClick={() => onFollow(item)}>Artikel anzeigen<ChevronRight size={13}/></button>}
        <ScreenLink href={item.href} onNavigate={onNavigate}>{item.type === "article" ? "Zum Artikel" : item.type === "lot" ? "Zum Postenarbeitsplatz" : "Im Lager öffnen"}</ScreenLink>
      </div>
    </li>)}</ul>}
    {result.has_more && <p className="wa-notice" data-testid="assistant-more">Die ersten fünf Treffer sind sichtbar. Suche eingrenzen oder den Arbeitsplatz öffnen.</p>}
    {result.articles?.map(article => <article className="wa-help-article" key={article.id}>
      <h3><BookOpen size={17}/>{article.title}</h3><p>{article.body}</p>
      <p className="wa-help-limit">{article.limitation}</p>
      <div className="wa-source"><span><Check size={13}/>Geprüfte Bedienhilfe · v{article.version} · {article.checked_at}</span><ScreenLink href={article.screen} onNavigate={onNavigate}>Zum Arbeitsplatz</ScreenLink></div>
    </article>)}
    {result.source && <div className="wa-source"><span><Check size={13}/>{result.source.title}</span><ScreenLink href={result.source.screen} onNavigate={onNavigate}>Quelle öffnen</ScreenLink></div>}
    {result.read_at && <p className="wa-timestamp">{result.kind === "help" ? "Hilfe abgerufen" : "Daten abgerufen"}: {date(result.read_at)} · lokale Antwort</p>}
  </div>;
}
